import { ZodError } from 'zod/v4'

import type { ZodType } from 'zod/v4'

/**
 * Validates a payload against a Zod schema and returns the validated data
 * @param validator - The Zod schema to validate against
 * @param payload - The payload to validate
 * @returns The validated data or throws an error with a 400 status code
 */
export function validatePayload<T>(validator: ZodType<T>, payload: unknown): T {
	try {
		return validator.parse(payload)
	} catch (error) {
		if (error instanceof ZodError) {
			console.error('Validation error:', error.message)
			throw new Error(error.message)
		}
		console.error('Invalid payload:', error)
		throw new Error('Invalid payload')
	}
}

/**
 * Validates a form value against a Zod schema and returns any validation errors
 * This is a drop-in replacement for formik's withZodSchema
 * @param validator - The Zod schema to validate against
 * @param values - The form values to validate
 * @returns An object containing any validation errors, or undefined if validation passes
 */
export function validateForm<T>(
	validator: ZodType<T>,
	values: unknown
): Record<string, string> | undefined {
	try {
		validator.parse(values)
		return undefined
	} catch (error) {
		if (error instanceof ZodError) {
			return error.issues.reduce(
				(acc, curr) => {
					const path = curr.path.join('.')
					acc[path] = curr.message
					return acc
				},
				{} as Record<string, string>
			)
		}
		return { form: 'Invalid form data' }
	}
}
